import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PROJECT_STATUSES, PRIORITIES } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Plus, FolderKanban, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export default function Projects() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/projects").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/settings").then((r) => setSettings(r.data)); api.get("/employees").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const schema = [
    { name: "nom", label: "Nom du projet", required: true, col: 2 },
    { name: "description", label: "Description", type: "textarea" },
    { name: "categorie", label: "Catégorie", type: "select", options: settings.project_categories || [] },
    { name: "responsable_name", label: "Responsable" },
    { name: "priorite", label: "Priorité", type: "select", options: PRIORITIES },
    { name: "statut", label: "Statut", type: "select", options: PROJECT_STATUSES },
    { name: "date_debut", label: "Date de début", type: "date" },
    { name: "date_fin_prevue", label: "Date de fin prévue", type: "date" },
    { name: "budget_prevu", label: "Budget prévu (FCFA)", type: "number" },
    { name: "avancement", label: "Avancement (%)", type: "number" },
  ];

  const save = async (form) => {
    try {
      if (editing) await api.put(`/projects/${editing.id}`, form);
      else await api.post("/projects", form);
      toast.success("Projet enregistré"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce projet et ses tâches ?")) return; await api.delete(`/projects/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <PageHeader title="Projets internes" subtitle="Initiatives et travaux internes d'OMNIBAT (hors chantiers clients)">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-project" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau projet</Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={FolderKanban} title="Aucun projet interne" subtitle="Créez un projet interne : site web, campagne marketing, recrutement…" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => {
            const pct = p.budget_prevu ? Math.round((p.budget_consomme / p.budget_prevu) * 100) : 0;
            return (
              <div key={p.id} className="rounded-xl border bg-white p-5 card-hover cursor-pointer" data-testid={`project-card-${p.id}`} onClick={() => nav(`/projets/${p.id}`)}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-[10px] text-slate-400">{p.numero}</p>
                    <h3 className="font-head font-bold text-slate-900 leading-tight">{p.nom}</h3>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => remove(p.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <StatusBadge value={p.statut} /><StatusBadge value={p.categorie} /><StatusBadge value={p.priorite} />
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">Avancement</span><span className="font-mono font-semibold">{p.avancement}%</span></div>
                  <Progress value={p.avancement} className="h-2" />
                </div>
                <div className="mt-3 pt-3 border-t flex justify-between text-xs">
                  <div><p className="text-slate-400">Budget</p><p className="font-mono font-semibold">{fmtMoney(p.budget_prevu)}</p></div>
                  <div className="text-right"><p className="text-slate-400">Consommé {pct}%</p><p className={`font-mono font-semibold ${pct > 100 ? "text-rose-600" : ""}`}>{fmtMoney(p.budget_consomme)}</p></div>
                </div>
                <p className="text-xs text-slate-500 mt-3">Responsable : <span className="font-medium text-slate-700">{p.responsable_name || "—"}</span></p>
              </div>
            );
          })}
        </div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le projet" : "Nouveau projet interne"} schema={schema} initial={editing} onSubmit={save} />
    </div>
  );
}
